/**
 * StripePaymentElementForm
 *
 * The shared Stripe **Payment Element** form used by every app (mobile, admin,
 * web2) to collect and save a payment instrument via a client-confirmed
 * SetupIntent. It is method-agnostic — it renders whatever the SetupIntent
 * enables:
 *   - a `card` intent → card fields (CVC/3DS handled inline on confirm)
 *   - a `us_bank_account` (ACH) intent → the account fields, the Financial
 *     Connections connect step, AND the Nacha mandate authorization (so we don't
 *     hand-roll inputs or own the mandate copy)
 *
 * On confirm it hands the resulting `seti_…` back through `onConfirmed`; the app
 * owns the server-side persist (`POST /confirm`).
 *
 * Which methods appear (card, or the card + bank accordion) is resolved
 * SERVER-SIDE — the SetupIntent's `payment_method_types` come from the 3-layer
 * resolver + the `ACH_SETUP_ENABLED` go-live flag — so the app just fetches the
 * one intent (`useStripeSetupIntent`) for its `clientSecret` + `publishableKey`
 * and renders whatever it enabled. There is no client-side ACH gate.
 *
 * @module domains/billing/StripePaymentElementForm
 */

import { useEffect, useRef, useState } from 'react'
import { cn } from '#utils'
import { useStripePaymentElement } from '../../../hooks/payment'
import type { ConfirmSetupResult, PaymentErrorCode } from '../../../hooks/payment'

export interface StripePaymentElementFormProps {
  /**
   * SetupIntent `client_secret` from the backend. Which methods it enables (card,
   * or the card + bank accordion) is resolved SERVER-SIDE — the client does not
   * choose.
   */
  clientSecret: string
  /** The user's HOME club's Stripe publishable key (same account as the intent). */
  publishableKey: string
  /**
   * Prefill for the account holder name. Applies only where the element renders a
   * name field — it does for `us_bank_account`, but NOT for card under the default
   * field config, so on a card-only intent this is inert. See the hook's
   * `defaultValues` doc.
   */
  defaultName?: string
  /** Prefill for the email (e.g. the member's account email). */
  defaultEmail?: string
  /** Optional Stripe Elements `appearance` object to theme the Payment Element. */
  appearance?: object
  /** `return_url` for confirm (defaults to the current URL; no navigation for card/ACH-instant). */
  returnUrl?: string
  /**
   * Called with the confirmed `seti_…` once the instrument verifies. The app
   * persists it server-side (`POST /confirm`) and shows success. While the
   * returned promise is pending the form stays in its submitting state.
   *
   * Resolving is an IRREVOCABLE SUCCESS SIGNAL: this form clears its submitting
   * state, renders no error, and leaves the host open. So a handler that hits a
   * failed precondition MUST THROW — never `return`. By the time this runs the
   * single-use SetupIntent is already spent at Stripe, so a bare return strands
   * an instrument that is attached there but absent from our DB, with nothing
   * shown to the user. Throwing surfaces the message — inline here, AND to the
   * host via {@link StripePaymentElementFormProps.onPersistError}.
   *
   * ```ts
   * onConfirmed={async (setupIntentId) => {
   *   if (!user?.id) throw new Error('Unable to save — no user session.')
   *   await persist(setupIntentId)   // let persist failures propagate too
   * }}
   * ```
   */
  onConfirmed: (setupIntentId: string) => void | Promise<void>
  /**
   * Called when confirmation fails, with the raw result — lets the app report
   * infrastructure failures (`result.isInfrastructureError`) to monitoring. The
   * form already renders the (optionally mapped) error inline.
   */
  onConfirmError?: (result: ConfirmSetupResult) => void
  /**
   * Called when the awaited `onConfirmed` THROWS — i.e. the instrument verified at
   * Stripe but the app's own persist failed. Distinct from `onConfirmError`, which
   * covers `confirmSetup` itself failing.
   *
   * ⚠ **A host that clears the SetupIntent on failure MUST use this.** The common
   * recovery is `retrySetupIntent(); throw err;` — but `retry()` nulls the intent,
   * and a host rendering `setupIntent ? <Form/> : <placeholder/>` therefore unmounts
   * this form in the very commit that sets its inline error. The message is then
   * unpaintable, and the user sees a blank form after their card was charged to a
   * spent intent. This callback is the channel that survives that unmount, so the
   * host can own the message (its own error state, or a toast).
   *
   * Hosts that toast independently (web2's checkout pages) are already covered and
   * don't need it. Hosts that rely on the form's inline error do.
   */
  onPersistError?: (error: Error) => void
  /**
   * Called ONCE when the element fails to load or initialize (Stripe.js blocked by
   * an ad-blocker/proxy, container missing, mount threw). The form renders the
   * message inline; this exists so the app can report it.
   *
   * Needed because `onConfirmError` cannot cover this: it only fires after Submit,
   * and Submit is disabled until the element is ready — so a load failure is
   * structurally unreachable from there. Without this prop a whole tenant can be
   * unable to add a card while monitoring shows nothing.
   */
  onLoadError?: (info: { error: string; code: PaymentErrorCode }) => void
  /**
   * Optional mapper from a failed confirm result to the user-facing message shown
   * inline. Lets a caller substitute branded copy for raw Stripe text — e.g. card
   * flows inject `mapStripeCardError` for friendly decline copy. When omitted the
   * raw `result.error` is shown (fine for ACH/FC, which has no card-decline copy).
   */
  mapConfirmError?: (result: ConfirmSetupResult) => string
  /** Submit button label. Defaults to "Save payment method". */
  submitLabel?: string
  /**
   * Label while the save is in flight. Defaults to "Saving…" — a bank-only surface
   * can pass "Connecting…" for the Financial Connections handoff.
   */
  submittingLabel?: string
  /** `data-testid` for the submit button (per-app e2e id). Defaults to "payment-element-save-btn". */
  submitTestId?: string
  /**
   * Called with the neutral selected method (`'card' | 'bank'`) when the user
   * switches rows in the accordion — lets the page make a fee/total line react.
   */
  onMethodChange?: (method: 'card' | 'bank') => void
  /**
   * Notifies the app while the save is in flight — `true` from the moment Submit is
   * pressed until BOTH `stripe.confirmSetup` (3DS / Financial Connections) and the
   * awaited `onConfirmed` persist have settled.
   *
   * Apps MUST use this to block dismissal/navigation for the duration: the
   * SetupIntent is single-use, so unmounting mid-flight spends it while destroying
   * the code that would have persisted the result — the instrument ends up attached
   * at Stripe but absent from our DB. Only this component knows that window.
   */
  onSubmittingChange?: (submitting: boolean) => void
  /**
   * Renders the container as a `<form id={formId}>` so a CTA elsewhere on the page
   * can submit it natively via `form={formId}`. Omit and the container stays a
   * `<div>` — safe to nest inside a host's own form.
   */
  formId?: string
  /** Hide the internal submit button — for hosts rendering their own CTA via `formId`. */
  hideSubmitButton?: boolean
  /**
   * Fires with whether a submit would actually confirm — the element is ready AND
   * its fields are complete (the same gate as the internal button) — and `false`
   * on unmount. An external CTA must gate on this: submit is a silent no-op otherwise.
   */
  onReadyChange?: (ready: boolean) => void
  /** Optional extra classes for the outer container. */
  className?: string
}

/**
 * Presentational Payment Element form. Owns only the element mount + submit
 * lifecycle; the app owns gating, intent-fetching, and persistence.
 */
export function StripePaymentElementForm({
  clientSecret,
  publishableKey,
  defaultName,
  defaultEmail,
  appearance,
  returnUrl,
  onConfirmed,
  onConfirmError,
  onPersistError,
  onLoadError,
  mapConfirmError,
  submitLabel = 'Save payment method',
  submittingLabel = 'Saving…',
  submitTestId = 'payment-element-save-btn',
  onMethodChange,
  onSubmittingChange,
  formId,
  hideSubmitButton,
  onReadyChange,
  className,
}: StripePaymentElementFormProps) {
  const {
    paymentElementRef,
    isReady,
    isComplete,
    isLoading,
    error: elementError,
    errorCode: elementErrorCode,
    confirmSetup,
  } = useStripePaymentElement({
      publishableKey,
      clientSecret,
      appearance,
      returnUrl,
      onMethodChange,
      defaultValues:
        defaultName || defaultEmail
          ? { billingDetails: { name: defaultName, email: defaultEmail } }
          : undefined,
    })

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Held in a ref so the unmount cleanup below doesn't re-run on identity changes.
  const onSubmittingChangeRef = useRef(onSubmittingChange)
  onSubmittingChangeRef.current = onSubmittingChange

  // Defence in depth: if this form is ever unmounted mid-flight, release the app's
  // busy lock. A stuck lock would leave the host drawer/sheet permanently
  // undismissable — a worse failure than the dismissal it exists to prevent.
  useEffect(() => () => onSubmittingChangeRef.current?.(false), [])

  // Report a load/init failure once per failure episode. Ref-guarded so a re-render
  // can't duplicate it, and reset on recovery so a later retry that fails again is
  // still captured — same shape as admin's Square SDK reporting.
  const onLoadErrorRef = useRef(onLoadError)
  onLoadErrorRef.current = onLoadError
  const reportedLoadErrorRef = useRef(false)
  useEffect(() => {
    if (elementErrorCode && !reportedLoadErrorRef.current) {
      reportedLoadErrorRef.current = true
      onLoadErrorRef.current?.({
        error: elementError ?? 'Payment form failed to load',
        code: elementErrorCode,
      })
    } else if (!elementErrorCode) {
      reportedLoadErrorRef.current = false
    }
  }, [elementErrorCode, elementError])

  // Tell an external CTA whether submitting would do anything. Ref'd like the
  // callbacks above so a new identity doesn't re-fire; `false` on unmount.
  const canSubmit = isReady && isComplete
  const onReadyChangeRef = useRef(onReadyChange)
  onReadyChangeRef.current = onReadyChange
  useEffect(() => {
    onReadyChangeRef.current?.(canSubmit)
  }, [canSubmit])
  useEffect(() => () => onReadyChangeRef.current?.(false), [])

  const handleSubmit = async () => {
    if (!isReady || !isComplete || submitting) return
    setSubmitting(true)
    onSubmittingChange?.(true)
    setSubmitError(null)
    try {
      const result = await confirmSetup()
      if (!result.success || !result.setupIntentId) {
        onConfirmError?.(result)
        const message = mapConfirmError
          ? mapConfirmError(result)
          : result.error ?? 'Payment method was not saved. Please try again.'
        setSubmitError(message)
        return
      }
      // App persists + shows success. Keep `submitting` true across the awaited
      // persist so the button can't be double-fired on a slow network.
      await onConfirmed(result.setupIntentId)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to save payment method')
      // Hand it to the host FIRST. If the host's recovery nulls the SetupIntent it
      // unmounts this form in the same commit, making the inline error below
      // unpaintable — this callback is the only channel that survives.
      onPersistError?.(error)
      setSubmitError(error.message)
    } finally {
      setSubmitting(false)
      onSubmittingChange?.(false)
    }
  }

  const displayError = submitError ?? elementError

  const body = (
    <>
      {/* Stripe renders the card fields, or the bank fields + Financial
          Connections + the Nacha mandate, depending on the intent. */}
      <div ref={paymentElementRef} className="min-h-10" />
      {!isReady && !elementError && (
        <p className="text-xs text-butter-gray-400">Loading payment form…</p>
      )}

      {displayError && (
        // Announced, not just rendered: a decline appears without any focus change, so a
        // screen-reader user gets nothing otherwise (StyleAndA11y.mdx §4.5).
        <div
          role="alert"
          aria-live="assertive"
          className="text-sm text-butter-error-500 bg-butter-error-50 rounded-lg px-3 py-2"
        >
          {displayError}
        </div>
      )}

      {!hideSubmitButton && (
        <button
          type="button"
          data-testid={submitTestId}
          onClick={handleSubmit}
          // Incomplete fields keep the button disabled but NOT relabelled — the label
          // ternary below stays keyed on readiness, so an unfilled form reads
          // "Save payment method", not "Initializing…".
          disabled={!canSubmit || submitting}
          className="w-full rounded-full py-4 text-base font-semibold text-white transition-colors bg-theme-600 text-interactive:bg-theme-700 disabled:bg-butter-gray-200 disabled:text-butter-gray-400"
        >
          {submitting ? submittingLabel : isLoading || !isReady ? 'Initializing…' : submitLabel}
        </button>
      )}
    </>
  )

  const containerClass = cn('flex flex-col gap-4', className)
  return formId ? (
    <form
      id={formId}
      className={containerClass}
      onSubmit={(event) => {
        event.preventDefault()
        void handleSubmit()
      }}
    >
      {body}
    </form>
  ) : (
    <div className={containerClass}>{body}</div>
  )
}
